/**
 * The global interaction patterns. UF_00 section 6.
 *
 * These exist so that the user, the operator, and the regulator read THE SAME WORDS FOR THE SAME
 * STATE. That is not a style preference. When a participant says "it says held in escrow" and the
 * support agent's console says "locked" and the regulator's export says "reserved", a dispute takes
 * three times as long and somebody eventually decides the platform is lying to one of them.
 */

import { normalizeAmountInput } from '@/lib/money';

/**
 * 6.2 THE ESCROW STATUS PATTERN.
 *
 * "Any held value shows one of a small set of states in plain language: held in escrow, awaiting
 * counterparty, released, refunded, or under dispute. The same badge and language are used
 * everywhere."
 *
 * A SMALL set, closed. If a sixth state is ever needed, that is a change to the escrow state machine
 * in the backend, and it should be as hard to add here as it is there.
 */
export type EscrowState =
  'held' | 'awaiting_counterparty' | 'released' | 'refunded' | 'under_dispute';

const ESCROW: Record<EscrowState, { label: string; tone: string; help: string }> = {
  held: {
    label: 'Held in escrow',
    tone: 'bg-info/10 text-info border-info/30',
    // The reassurance IS the feature. Money that has left your balance and not arrived anywhere is
    // terrifying if nobody tells you where it is.
    help: 'Your money is set aside and protected while this completes. It has not been sent yet.',
  },
  awaiting_counterparty: {
    label: 'Awaiting the other side',
    tone: 'bg-warning/10 text-warning border-warning/30',
    help: 'We are waiting for the other person to do their part. Your money is still protected.',
  },
  released: {
    label: 'Released',
    tone: 'bg-accent/10 text-accent border-accent/30',
    help: 'This completed and the money has been sent.',
  },
  refunded: {
    label: 'Refunded',
    tone: 'bg-ink/5 text-ink-muted border-border',
    help: 'This did not complete, so the money came back to you.',
  },
  under_dispute: {
    label: 'Under dispute',
    tone: 'bg-danger/10 text-danger border-danger/30',
    help: 'The money stays protected until this is resolved. Nobody can move it in the meantime.',
  },
};

export function EscrowBadge({ state }: { state: EscrowState }) {
  const s = ESCROW[state];

  return (
    <span
      title={s.help}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.tone}`}
    >
      <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      {s.label}
    </span>
  );
}

export function EscrowExplainer({ state }: { state: EscrowState }) {
  return <p className="text-sm text-ink-muted">{ESCROW[state].help}</p>;
}

/**
 * 6.3 THE CAP AND LIMIT PATTERN.
 *
 * "Where a cap applies, the app shows the user their remaining headroom BEFORE they compose, and
 * warns as they approach it. A blocked action from a cap explains WHICH cap and WHEN IT RESETS."
 *
 * Shown before, not after. A cap discovered at the moment of payment is a cap that made the user look
 * foolish in front of whoever they were paying, and the platform knew all along.
 *
 * Amber at 80 percent, never red. A cap being approached is not an error — the participant has done
 * nothing wrong — and colouring it red teaches people that red means nothing.
 */
export function CapHeadroom({
  label,
  used,
  limit,
  asset,
  resets,
}: {
  label: string;
  /** Minor units, as exact decimal strings. Never floats: this is the participant's money. */
  used: string;
  limit: string;
  asset: string;
  /** When the cap resets, in words a person uses: "midnight tonight", "1 August". */
  resets?: string;
}) {
  // BigInt, not Number. A daily cap in minor units can exceed 2^53 in a low-denomination currency,
  // and the one number in this component that must never be approximate is the one that decides
  // whether somebody's rent payment goes through.
  //
  // `normalizeAmountInput` first, because `BigInt("22,000")` THROWS, and a throw in a render is how
  // the withdraw screen came to show React Router's error page instead of a balance. These values are
  // already minor units, so this strips a stray separator and rescales nothing.
  const usedUnits = BigInt(normalizeAmountInput(used) || '0');
  const limitUnits = BigInt(normalizeAmountInput(limit) || '0');

  const pct = limitUnits === 0n ? 100 : Number((usedUnits * 100n) / limitUnits);
  const remaining = limitUnits > usedUnits ? limitUnits - usedUnits : 0n;

  const tone = pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-accent';

  return (
    <div className="rounded-card border border-border bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="tabular text-xs text-ink-muted">
          {asset} {money(remaining)} left
        </span>
      </div>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className={`h-full rounded-full transition-all ${tone}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>

      <p className="mt-2 text-xs text-ink-muted">
        {pct >= 100
          ? 'You have reached this limit.'
          : pct >= 80
            ? 'You are close to this limit.'
            : `${asset} ${money(usedUnits)} of ${money(limitUnits)} used.`}
        {resets ? ` Resets ${resets}.` : ''}
      </p>
    </div>
  );
}

/** Minor units to a display string. Exact, integer arithmetic only. KES has two decimal places. */
function money(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;

  const major = abs / 100n;
  const cents = abs % 100n;

  const grouped = major.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}.${cents.toString().padStart(2, '0')}`;
}
